// Maintenance handling for embedded widgets (2026-10-03).
//
// The API answers 503 {"maintenance":true,"message":…,"estimatedReturn":…} while a deploy runs
// (MaintenanceMiddleware), and during the stop→start window it does not answer at all. The Web app
// sends the customer to /maintenance; a venue's own website cannot do that, so here every [data-365]
// mount is replaced by one "We're updating" card, `api/status` (exempt from maintenance) is polled
// every 10 s, and when the API is back the page reloads so the widgets mount fresh.
import type { ThemeOptions } from './types';
import { createRoot, h, footer } from './ui';

export interface MaintenanceInfo {
  message?: string | null;
  estimatedReturn?: string | null;
}

export class MaintenanceError extends Error {
  constructor(public info: MaintenanceInfo = {}) {
    super('365 Sports.Online is being updated');
    this.name = 'MaintenanceError';
  }
}

const DEFAULT_MESSAGE = "We're updating 365 Sports.Online. This usually takes a few minutes.";
const POLL_MS = 10_000;

let apiBase = '';
let theme: ThemeOptions = {};
let active = false;

/** Called once by the loader so a mid-session outage can take over the mounts. */
export function configureMaintenance(base: string, t: ThemeOptions): void {
  apiBase = base.replace(/\/+$/, '');
  theme = t;
}

/** True for the maintenance middleware's 503 (not any other 503). Reads a clone, so the body stays usable. */
export async function isMaintenanceResponse(res: Response): Promise<MaintenanceInfo | null> {
  if (res.status !== 503) return null;
  try {
    const body = (await res.clone().json()) as { maintenance?: boolean; message?: string; estimatedReturn?: string };
    return body && body.maintenance ? { message: body.message, estimatedReturn: body.estimatedReturn } : null;
  } catch {
    return null;
  }
}

/** Is the API unreachable or in maintenance right now? null = it is fine. */
export async function probeMaintenance(base = apiBase): Promise<MaintenanceInfo | null> {
  try {
    const res = await fetch(`${base}/api/status`, { cache: 'no-store' });
    if (!res.ok) return { };
    const s = (await res.json()) as { maintenance?: boolean; message?: string; estimatedReturn?: string };
    return s.maintenance ? { message: s.message, estimatedReturn: s.estimatedReturn } : null;
  } catch {
    return { }; // no answer at all (stopped app, DNS, offline)
  }
}

/**
 * A fetch failed. Decide whether the API is down (→ maintenance card + MaintenanceError) or whether it
 * was an ordinary error (→ returns null and the caller handles it as before).
 */
export async function handleFetchFailure(res: Response | null): Promise<MaintenanceError | null> {
  if (!navigator.onLine) return null; // the customer's own connection; not our outage
  const info = res ? await isMaintenanceResponse(res) : await probeMaintenance();
  if (!info) return null;
  showMaintenance(info);
  return new MaintenanceError(info);
}

function whenText(estimatedReturn?: string | null): string | null {
  if (!estimatedReturn) return null;
  const d = new Date(estimatedReturn);
  if (isNaN(d.getTime())) return `Expected back: ${estimatedReturn}`;
  try {
    return `Expected back around ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
  } catch {
    return null;
  }
}

/** Replace every widget mount with the maintenance card and start polling. Idempotent. */
export function showMaintenance(info: MaintenanceInfo = {}, mounts?: HTMLElement[]): void {
  if (active) return;
  active = true;
  const targets = mounts ?? (Array.from(document.querySelectorAll('[data-365]')) as HTMLElement[]);
  const msg = (info.message || '').trim() || DEFAULT_MESSAGE;
  const when = whenText(info.estimatedReturn);

  const statusEls: HTMLElement[] = [];
  for (const mount of targets) {
    try {
      const { root } = createRoot(mount, theme);
      const status = h('div', { class: 'e365-muted', style: 'font-size:.85rem;margin-top:6px' }, 'Checking again every few seconds…');
      statusEls.push(status);
      root.append(
        h('div', { class: 'e365-alert info', role: 'status', 'aria-live': 'polite' },
          h('div', { style: 'font-weight:600;margin-bottom:4px' }, "We're updating"),
          h('div', undefined, msg),
          when ? h('div', { style: 'margin-top:4px' }, when) : null,
          status,
        ),
        footer(),
      );
    } catch { /* a mount we cannot draw into; keep going */ }
  }
  try { window.dispatchEvent(new CustomEvent('embed365:maintenance', { detail: info })); } catch { /* ignore */ }

  let tries = 0;
  const tick = async (): Promise<void> => {
    tries++;
    const still = await probeMaintenance();
    if (!still) {
      for (const s of statusEls) s.textContent = 'Back online — reloading…';
      try { location.reload(); } catch { /* ignore */ }
      return;
    }
    if (still.message && still.message !== msg) {
      // The venue/admin may refine the message while we wait; keep the card current.
      for (const s of statusEls) s.textContent = still.message;
    } else if (tries % 6 === 0) {
      for (const s of statusEls) s.textContent = `Still updating… (${Math.round(tries * POLL_MS / 60000)} min)`;
    }
    setTimeout(() => void tick(), POLL_MS);
  };
  setTimeout(() => void tick(), POLL_MS);
}
